import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Feather, LogIn, LogOut, ShieldCheck, Bell, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose,
} from "@/components/ui/sheet";

const publicLinks = [
  { to: "/", label: "Home", end: true },
  { to: "/wizard", label: "Nuova valutazione" },
];

const adminLinks = [
  { to: "/admin/crm", label: "CRM" },
  { to: "/admin/leads", label: "Leads" },
  { to: "/admin/orders", label: "Ordini" },
  { to: "/verbale-accesso", label: "Verbale" },
  { to: "/custody", label: "Custody" },
  { to: "/sample-protocol", label: "Saggio" },
];

export default function Header() {
  const { user, isAdmin, login, logout } = useAuth();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin) { setUnread(0); return; }
    const load = () => api.get("/admin/leads/unread-count", { withCredentials: true })
      .then((r) => setUnread(r.data.unread || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    const onLeadsRead = () => setUnread(0);
    window.addEventListener("leads:read", onLeadsRead);
    return () => {
      clearInterval(t);
      window.removeEventListener("leads:read", onLeadsRead);
    };
  }, [isAdmin]);

  const handleMobileNav = (to) => {
    setMobileOpen(false);
    navigate(to);
  };

  return (
    <header className="border-b hairline bg-white sticky top-0 z-40" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
          <div className="w-8 h-8 border hairline flex items-center justify-center bg-navy text-white">
            <Feather size={16} strokeWidth={1.5} />
          </div>
          <div className="leading-tight">
            <div className="font-serif text-lg sm:text-xl font-semibold tracking-tight">Perizia Ready</div>
            <div className="overline hidden sm:block">PeriziaFirma.it</div>
          </div>
        </Link>

        {/* Desktop nav (≥ md) */}
        <nav className="hidden md:flex items-center gap-1">
          {publicLinks.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  isActive ? "border-navy text-ink" : "border-transparent text-slate-600 hover:text-ink hover:border-slate-300"
                }`
              }>
              {l.label}
            </NavLink>
          ))}
          {isAdmin && adminLinks.map((l) => (
            <NavLink key={l.to} to={l.to}
              data-testid={`nav-admin-${l.to.split("/").pop()}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium border-b-2 transition-colors relative ${
                  isActive ? "border-navy text-navy" : "border-transparent text-navy/70 hover:text-navy hover:border-navy/40"
                }`
              }>
              {l.label}
              {l.to === "/admin/leads" && unread > 0 && (
                <span data-testid="unread-badge"
                  className="ml-1 inline-flex items-center gap-0.5 bg-forensic-red text-white text-[10px] font-mono px-1.5 py-0.5 rounded-sm align-top">
                  <Bell size={9} /> {unread}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2">
          {/* Desktop auth controls */}
          <div className="hidden md:flex items-center gap-2">
            {isAdmin ? (
              <>
                <span className="inline-flex items-center gap-1 border border-navy text-navy px-3 py-1.5 rounded-sm text-xs" data-testid="admin-badge">
                  <ShieldCheck size={12} /> Admin · {user?.email}
                </span>
                <button onClick={logout} data-testid="logout-btn"
                  className="inline-flex items-center gap-2 text-slate-500 hover:text-forensic-red text-xs px-2 py-1.5">
                  <LogOut size={14} /> Esci
                </button>
              </>
            ) : (
              <button onClick={login} data-testid="login-btn"
                className="inline-flex items-center gap-2 border hairline hover:border-navy text-slate-600 hover:text-ink px-4 py-2 rounded-sm text-xs font-medium transition-colors">
                <LogIn size={14} /> Login amministratore
              </button>
            )}
          </div>

          {/* Mobile: admin unread indicator (subtle) + hamburger */}
          {isAdmin && unread > 0 && (
            <span data-testid="mobile-unread-dot"
              className="md:hidden inline-flex items-center gap-1 bg-forensic-red text-white text-[10px] font-mono px-1.5 py-0.5 rounded-sm">
              <Bell size={9} /> {unread}
            </span>
          )}

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="mobile-menu-btn"
                aria-label="Apri menu"
                className="md:hidden inline-flex items-center justify-center w-10 h-10 border hairline rounded-sm text-ink hover:border-navy transition-colors"
              >
                <Menu size={18} strokeWidth={1.5} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm p-0 flex flex-col" data-testid="mobile-menu-sheet">
              <SheetHeader className="px-5 py-4 border-b hairline text-left">
                <SheetTitle className="font-serif text-lg">Menu</SheetTitle>
                {isAdmin && (
                  <div className="mt-2 inline-flex items-center gap-1 border border-navy text-navy px-2 py-1 rounded-sm text-[11px] w-fit" data-testid="mobile-admin-badge">
                    <ShieldCheck size={11} /> Admin · {user?.email}
                  </div>
                )}
              </SheetHeader>

              <div className="flex-1 overflow-y-auto px-2 py-3">
                <div className="px-3 pb-2 overline text-slate-500">Pubblico</div>
                {publicLinks.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <button
                      type="button"
                      onClick={() => handleMobileNav(l.to)}
                      data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                      className="w-full text-left px-3 py-3 text-sm font-medium text-ink hover:bg-slate-50 rounded-sm transition-colors"
                    >
                      {l.label}
                    </button>
                  </SheetClose>
                ))}

                {isAdmin && (
                  <>
                    <div className="mt-4 px-3 pb-2 overline text-navy">Admin</div>
                    {adminLinks.map((l) => (
                      <SheetClose asChild key={l.to}>
                        <button
                          type="button"
                          onClick={() => handleMobileNav(l.to)}
                          data-testid={`mobile-nav-admin-${l.to.split("/").pop()}`}
                          className="w-full text-left px-3 py-3 text-sm font-medium text-navy hover:bg-navy/5 rounded-sm transition-colors flex items-center justify-between"
                        >
                          <span>{l.label}</span>
                          {l.to === "/admin/leads" && unread > 0 && (
                            <span className="inline-flex items-center gap-0.5 bg-forensic-red text-white text-[10px] font-mono px-1.5 py-0.5 rounded-sm">
                              <Bell size={9} /> {unread}
                            </span>
                          )}
                        </button>
                      </SheetClose>
                    ))}
                  </>
                )}
              </div>

              <div className="border-t hairline p-3">
                {isAdmin ? (
                  <button
                    onClick={() => { setMobileOpen(false); logout(); }}
                    data-testid="mobile-logout-btn"
                    className="w-full inline-flex items-center justify-center gap-2 border hairline hover:border-forensic-red text-slate-600 hover:text-forensic-red px-4 py-3 rounded-sm text-sm font-medium transition-colors"
                  >
                    <LogOut size={14} /> Esci
                  </button>
                ) : (
                  <button
                    onClick={() => { setMobileOpen(false); login(); }}
                    data-testid="mobile-login-btn"
                    className="w-full inline-flex items-center justify-center gap-2 bg-navy text-white hover:bg-navy/90 px-4 py-3 rounded-sm text-sm font-medium transition-colors"
                  >
                    <LogIn size={14} /> Login amministratore
                  </button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
